import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { calcClosingCosts, calcPitia } from "@/lib/investor-calcs";
import closingCostHero from "@/assets/hero/hero-investment-property-closing.jpg";

const FAQS = [
  {
    q: "How much are closing costs on an investment property?",
    a: "They vary widely by state and program. Lender origination, title and settlement, appraisal, recording and transfer taxes, plus prepaid insurance, prepaid taxes and escrow reserves are the recurring line items. Enter your own figures here rather than relying on a national average.",
  },
  {
    q: "What is the difference between closing costs and prepaids?",
    a: "Closing costs are one-time charges for originating and closing the loan. Prepaids and reserves are your own future expenses collected early — insurance premiums, property taxes, and escrow cushion. Both are cash at closing, but only closing costs are a cost of borrowing.",
  },
  {
    q: "Can I roll closing costs into a DSCR loan?",
    a: "On a refinance, costs are commonly financed inside the new loan subject to the LTV cap. On a purchase they are generally paid at closing, though seller credits and lender credits can offset them.",
  },
  {
    q: "What are escrow reserves?",
    a: "Months of taxes and insurance collected upfront so the escrow account can pay bills when due. This tool models reserves as a number of months of full PITIA, which is the conservative approach.",
  },
  {
    q: "Is this a Loan Estimate?",
    a: "No. This is an educational estimating tool with assumptions you enter. It is not a Loan Estimate, not an offer of credit, and not a commitment to lend. Actual figures come from a completed application and lender disclosures.",
  },
  {
    q: "What is origination fee versus discount points on a closing cost worksheet?",
    a: "An origination fee compensates the broker or lender for processing and underwriting the loan and is charged regardless of the interest rate chosen, while discount points are optional upfront charges paid specifically to lower the note rate. Both appear as a percentage of loan amount and both add to total cash needed at closing.",
  },
  {
    q: "Are lender credits the same as seller credits?",
    a: "No. A lender credit typically comes from accepting a higher interest rate in exchange for reduced upfront costs, while a seller credit is negotiated as part of the purchase contract and applied toward the buyer's closing costs or prepaids at settlement. Both reduce cash needed to close but do so through different mechanisms with different tradeoffs.",
  },
  {
    q: "Why do investment property closing costs run higher than owner-occupied costs?",
    a: "Non-owner-occupied and DSCR loans commonly carry higher origination pricing and may require larger escrow reserves and higher title insurance premiums than a comparable primary-residence purchase, reflecting the added risk profile lenders assign to investment property. Down payment requirements are also higher, which increases total cash to close even though it is not technically a closing cost.",
  },
  {
    q: "How much should I budget for title insurance on an investment property purchase?",
    a: "Title insurance premiums vary by state, purchase price, and whether it is a rate-regulated or filed-rate state, so there is no single national figure. Enter a quote from a local title company into this calculator rather than a generic assumption, since some states set rates by regulation and others allow competitive pricing.",
  },
  {
    q: "Can closing costs be higher than expected at the final walkthrough?",
    a: "Yes — property tax prorations, HOA transfer fees, and last-minute lender fee changes can shift cash-to-close between the initial estimate and the closing disclosure. This calculator is an educational planning tool, not a Loan Estimate or Closing Disclosure, so treat its output as a starting budget to refine as you get vendor quotes.",
  },
  {
    q: "Do I need reserves in addition to closing costs on a DSCR loan?",
    a: "Many DSCR programs require post-closing liquid reserves — often several months of the new PITIA payment held in the borrower's accounts — separate from the escrow reserves collected at closing. Reserve requirements vary by program, credit profile, and number of financed properties, so confirm current requirements with your loan officer.",
  },
  {
    q: "How does loan-to-value affect total cash needed at closing?",
    a: "A lower LTV means a larger down payment, which is typically the biggest driver of total cash to close even though it is not technically a closing cost. Reducing LTV can also lower the rate and sometimes the mortgage insurance or risk-based pricing adjustments, partially offsetting the larger down payment through smaller ongoing carrying costs.",
  },
];

export const Route = createFileRoute("/closing-cost-calculator")({
  head: () =>
    buildHead({
      title: "Investment Property Closing Cost Calculator | Simply Approved",
      description:
        "Estimate cash to close on an investment property: down payment, origination, title, appraisal, recording, prepaid taxes and insurance, and escrow reserves.",
      url: "/closing-cost-calculator",
      keywords:
        "closing cost calculator investment property, cash to close calculator, DSCR loan closing costs, rental property closing costs, prepaid escrow reserves calculator, title and settlement cost estimate, investor loan origination fee",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Closing Cost Calculator", url: "/closing-cost-calculator" },
      ],
      service: { name: "Closing Cost Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Closing Cost Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/closing-cost-calculator",
          description: "Cash-to-close and closing cost estimator for investment property financing.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: ClosingPage,
});

type Inputs = {
  price: number;
  downPct: number;
  ratePct: number;
  termYears: number;
  originationPct: number;
  lenderFlatFees: number;
  title: number;
  appraisal: number;
  recordingPct: number;
  insAnnual: number;
  prepaidInsMonths: number;
  taxesAnnual: number;
  prepaidTaxMonths: number;
  reserveMonths: number;
  hoaMonthly: number;
  earnestMoney: number;
  sellerCredits: number;
};

const DEFAULT: Inputs = {
  price: 400000,
  downPct: 25,
  ratePct: 7.5,
  termYears: 30,
  originationPct: 0,
  lenderFlatFees: 0,
  title: 0,
  appraisal: 0,
  recordingPct: 0.6,
  insAnnual: 2000,
  prepaidInsMonths: 12,
  taxesAnnual: 4400,
  prepaidTaxMonths: 3,
  reserveMonths: 2,
  hoaMonthly: 0,
  earnestMoney: 5000,
  sellerCredits: 0,
};

function ClosingPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("closing_cost", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("closing_cost", String(k), v as never);
  };

  const m = useMemo(() => {
    const down = i.price * (i.downPct / 100);
    const loan = Math.max(0, i.price - down);
    const pitia = calcPitia({
      loanAmount: loan,
      ratePct: i.ratePct,
      termYears: i.termYears,
      taxesAnnual: i.taxesAnnual,
      insuranceAnnual: i.insAnnual,
      hoaMonthly: i.hoaMonthly,
    });
    const c = calcClosingCosts({
      purchasePrice: i.price,
      downPaymentPct: i.downPct,
      originationPct: i.originationPct,
      lenderFlatFees: i.lenderFlatFees,
      titleAndSettlement: i.title,
      appraisal: i.appraisal,
      recordingAndTransferPct: i.recordingPct,
      prepaidInsuranceMonths: i.prepaidInsMonths,
      insuranceAnnual: i.insAnnual,
      prepaidTaxMonths: i.prepaidTaxMonths,
      taxesAnnual: i.taxesAnnual,
      escrowReserveMonths: i.reserveMonths,
      monthlyPitia: pitia.pitia,
      earnestMoney: i.earnestMoney,
      sellerCredits: i.sellerCredits,
    });
    const pctOfPrice = i.price > 0 ? c.totalClosingCosts / i.price : 0;
    return { pitia, c, pctOfPrice };
  }, [i]);

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("closing_cost", { cash_to_close: Math.round(m.c.estimatedCashToClose) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Purchase price", value: usd(i.price) },
    { label: "Loan amount", value: usd(m.c.loanAmount) },
    { label: "Down payment", value: usd(m.c.downPayment) },
    { label: "Closing costs", value: usd(m.c.totalClosingCosts) },
    { label: "Prepaids & reserves", value: usd(m.c.totalPrepaidsAndReserves) },
    { label: "Estimated cash to close", value: usd(m.c.estimatedCashToClose) },
    { label: "Closing costs as % of price", value: `${(m.pctOfPrice * 100).toFixed(2)}%` },
  ];

  return (
    <CalculatorScaffold
      calculator="closing_cost"
      eyebrow="Closing costs & cash to close"
      title="Investment Property Closing Cost Calculator"
      subtitle="Build cash to close line by line: down payment, lender and third-party fees, prepaid taxes and insurance, and escrow reserves. This is an educational estimate — not a Loan Estimate, an offer of credit, or a commitment to lend."
      shareUrl={shareUrl}
      shareTitle="My estimated cash to close"
      heroImage={{
        src: closingCostHero,
        alt: "Real estate investor signing closing documents for a newly purchased rental property",
      }}
      inputs={
        <>
          <CalcCard title="1. Purchase & loan">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Purchase price" value={i.price} onChange={(v) => set("price", v)} prefix="$" />
              <CalcField label="Down payment" value={i.downPct} onChange={(v) => set("downPct", v)} suffix="%" step={1} />
              <CalcField label="Note rate" value={i.ratePct} onChange={(v) => set("ratePct", v)} suffix="%" step={0.125} hint="Scenario assumption you enter — not a quoted Simply Approved Mortgages rate. Used only to size the payment and escrow reserve." />
              <CalcField label="Amortization (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={5} />
            </div>
          </CalcCard>
          <CalcCard title="2. Lender & third-party fees">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Origination" value={i.originationPct} onChange={(v) => set("originationPct", v)} suffix="%" step={0.25} hint="Enter the figure from your own quote. Left at zero by default — we do not publish a standard origination charge here." />
              <CalcField label="Other lender fees (flat)" value={i.lenderFlatFees} onChange={(v) => set("lenderFlatFees", v)} prefix="$" hint="Underwriting, processing, doc prep. Enter the amounts on your own quote; these vary by lender and program." />
              <CalcField label="Title & settlement" value={i.title} onChange={(v) => set("title", v)} prefix="$" hint="Third-party charge — use your title company's quote." />
              <CalcField label="Appraisal" value={i.appraisal} onChange={(v) => set("appraisal", v)} prefix="$" hint="Third-party charge — varies by property type and market." />
              <CalcField label="Recording & transfer tax" value={i.recordingPct} onChange={(v) => set("recordingPct", v)} suffix="%" step={0.1} hint="Highly state-specific — check your county." />
            </div>
          </CalcCard>
          <CalcCard title="3. Prepaids, reserves & credits">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Insurance (annual)" value={i.insAnnual} onChange={(v) => set("insAnnual", v)} prefix="$" />
              <CalcField label="Insurance months collected" value={i.prepaidInsMonths} onChange={(v) => set("prepaidInsMonths", v)} step={1} />
              <CalcField label="Property taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" />
              <CalcField label="Tax months collected" value={i.prepaidTaxMonths} onChange={(v) => set("prepaidTaxMonths", v)} step={1} />
              <CalcField label="Escrow reserve (months of PITIA)" value={i.reserveMonths} onChange={(v) => set("reserveMonths", v)} step={1} />
              <CalcField label="HOA dues (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
              <CalcField label="Earnest money already paid" value={i.earnestMoney} onChange={(v) => set("earnestMoney", v)} prefix="$" />
              <CalcField label="Seller / lender credits" value={i.sellerCredits} onChange={(v) => set("sellerCredits", v)} prefix="$" />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label="Cash to close" value={usd(m.c.estimatedCashToClose)} />
            <CalcStat label="Down payment" value={usd(m.c.downPayment)} />
            <CalcStat label="Closing costs" value={usd(m.c.totalClosingCosts)} />
            <CalcStat label="Prepaids & reserves" value={usd(m.c.totalPrepaidsAndReserves)} />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">Line-item detail</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              <CalcRow label="Loan amount" value={usd(m.c.loanAmount)} />
              <CalcRow label="Down payment" value={usd(m.c.downPayment)} />
              <CalcRow label="Origination" value={usd(m.c.origination)} />
              <CalcRow label="Other lender fees" value={usd(i.lenderFlatFees)} />
              <CalcRow label="Title, settlement & appraisal" value={usd(i.title + i.appraisal)} />
              <CalcRow label="Recording & transfer tax" value={usd(m.c.recordingAndTransfer)} />
              <CalcRow label="Total closing costs" value={usd(m.c.totalClosingCosts)} strong />
              <CalcRow label="Prepaid insurance" value={usd(m.c.prepaidInsurance)} />
              <CalcRow label="Prepaid taxes" value={usd(m.c.prepaidTaxes)} />
              <CalcRow label="Escrow reserves" value={usd(m.c.escrowReserves)} />
              <CalcRow label="Total prepaids & reserves" value={usd(m.c.totalPrepaidsAndReserves)} strong />
              <CalcRow label="Less earnest money" value={`−${usd(i.earnestMoney)}`} />
              <CalcRow label="Less credits" value={`−${usd(i.sellerCredits)}`} />
              <CalcRow label="Estimated cash to close" value={usd(m.c.estimatedCashToClose)} strong colorClass="text-brand" />
              <CalcRow label="Estimated monthly PITIA" value={usd(m.pitia.pitia)} />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "Which of these costs are negotiable?",
        "What is the difference between closing costs and prepaids?",
        "How would seller credits change my cash to close?",
        "Why do recording and transfer taxes vary so much?",
      ]}
      methodologyIntro="Every figure is an assumption you control — no national averages are injected behind the scenes."
      methodology={
        <>
          <p>
            <strong>Structure.</strong> Cash to close = down payment + closing costs + prepaids and reserves − earnest
            money − credits. Origination is a percentage of the loan; recording and transfer tax is applied to price
            because most jurisdictions assess it that way.
          </p>
          <p>
            <strong>Reserves.</strong> Modeled as whole months of full PITIA, which is conservative. Some lenders
            collect only tax and insurance months; escrow waivers change the figure again.
          </p>
          <p>
            <strong>What it is not.</strong> Not a Loan Estimate under TILA/RESPA and not a fee quote. Actual fees come
            from the lender, title company, and county, and are disclosed after a completed application.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="Closing cost FAQ"
      related={[
        { to: "/dscr-payment-calculator", label: "DSCR payment (PITIA) calculator" },
        { to: "/cash-on-cash-return-calculator", label: "Cash-on-cash return calculator" },
        { to: "/cash-out-refinance-calculator", label: "Cash-out refinance calculator" },
        { to: "/dscr-loan-requirements", label: "DSCR loan requirements" },
        { to: "/process", label: "How the process works" },
        { to: "/apply", label: "Request a scenario review" },
      ]}
      ctaTitle="Know the real cash you need"
      ctaBody="Send the property and structure and a licensed loan officer will walk through the estimated costs for your business-purpose scenario."
    />
  );
}
